<template>
  <v-tooltip location="top">
    <template v-slot:activator="{ props }">
      <UpgradeInfoDialog :show-info="showVideoUpsell">
        <template v-slot="{ props: upgradeProps }">
          <div v-bind="{ ...props, ...upgradeProps }">
            <!-- TODO add outlined style -->
            <!-- :variant="!primary ? 'outlined' : undefined" -->
            <v-btn
              variant="outlined"
              :disabled="disabled || isLoading"
              :loading="isLoading"
              :class="'download-btn pointer ' + btnClass"
              :color="primary ? 'primary' : ''"
              data-product-fruits="content-creator-download-post"
              elevation="0"
              @click="downloadPost"
            >
              <v-icon size="18"> mdi-download </v-icon>
              <span
                v-if="showLabel"
                class="ml-1"
                >{{ $t('buttons.content-creator.download') }}</span
              >
            </v-btn>
          </div>
        </template>
      </UpgradeInfoDialog>
    </template>
    <span>{{ tooltip }}</span>
  </v-tooltip>
</template>

<script>
import UpgradeInfoDialog from '@/components/UpgradeInfoDialog.vue'
import { AnimationEvents } from '@/lib/animation'
import bus, { eventNames } from '@/lib/eventBus'
import { createAndDownloadZip } from '@/lib/generateZip'
import { transformToKebabCase } from '@/lib/kebabCase'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import brandingMixin from '@/mixins/branding'
import featureMixin from '@/mixins/feature'
import { buildUrlWithTracking } from '@/modules/contentCreator/creator/lib/urlWithTracking'
import MARK_SAVED_CONTENT_AS_DOWNLOADED from '@/modules/contentCreator/creator/queries/markSavedContentAsDownloaded.gql'

import SAVE_CONTENT from './queries/saveContent.gql'

export default {
  components: {
    UpgradeInfoDialog,
  },
  mixins: [featureMixin, brandingMixin],
  props: {
    primary: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isNotLicensedForDownload: {
      type: Boolean,
      default: false,
    },
    savedContentId: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    image: {
      type: String,
      default: '',
    },
    carouselImages: {
      type: Array,
      default: () => [],
    },
    imageSource: {
      type: String,
      default: '',
    },
    textKind: {
      type: String,
      default: '',
    },
    topic: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    topicId: {
      type: String,
      default: '',
    },
    btnClass: {
      type: String,
      default: '',
    },
    showLabel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
    }
  },
  computed: {
    isVideo() {
      return !!this.image?.match(/\.(mp4|mov|avi|wmv|webm)(\?|$)/i)
    },
    isPostVideoEnabled() {
      return this.getFeature(this.featureNames.CONTENT_CREATOR).config.postVideo
    },
    showVideoUpsell() {
      return this.isVideo && !this.isPostVideoEnabled
    },
    tooltip() {
      if (this.disabled) {
        if (this.isNotLicensedForDownload) {
          return this.$t('buttons.content-creator.tooltip.disable-download-license')
        }
        return this.$t('buttons.content-creator.tooltip.disable-download')
      }
      return this.$t('buttons.content-creator.tooltip.download-post')
    },
  },
  methods: {
    transformToKebabCase,
    async downloadPost() {
      if (this.showVideoUpsell) return
      const object = ['#all', '#today', '#week', '#month', '#month', '#year'].includes(
        this.$route.hash
      )
        ? 'Content History'
        : 'Content Creator'
      this.$tracking.event(object, 'Clicked', 'Download Content')

      this.isLoading = true
      await this.downloadZipFile()
      if (this.savedContentId) {
        await this.markSavedContentAsDownloaded()
      } else {
        await this.storeSavedContent()
      }
      this.$emit('contentSaved')
      this.isLoading = false
    },
    async storeSavedContent() {
      try {
        await this.$apollo.mutate({
          mutation: SAVE_CONTENT,
          variables: {
            input: {
              text: this.text ?? null,
              imagePath: this.image ?? null,
              imageSource: this.imageSource,
              textKind: this.textKind,
              topicId: this.topicId,
              url: buildUrlWithTracking(this.url, this.textKind, this.imageSource),
            },
          },
        })
        this.$tracking.event('Content Creator', 'Clicked', 'Download', this.textKind)
        bus.$emit(eventNames.TRIGGER_ANIMATION, AnimationEvents.CONTENT_DOWNLOADED)
        showSnackbarMessage('info', this.$t('alerts.content-creator.save-content.success'), [
          {
            text: this.$t('alerts.content-creator.save-content.success-link'),
            link: '/content-creator#all',
          },
        ])
      } catch (err) {
        showSnackbarMessage('error', this.$t('alerts.content-creator.save-content.error'))
      }
    },

    async markSavedContentAsDownloaded() {
      try {
        await this.$apollo.mutate({
          mutation: MARK_SAVED_CONTENT_AS_DOWNLOADED,
          variables: { id: this.savedContentId },
        })
      } catch (err) {
        showSnackbarMessage('error', this.$t('alerts.content-creator.save-content.error'))
      }
    },

    async downloadZipFile() {
      if (!this.text && !this.image) {
        showSnackbarMessage('error', this.$t('alerts.content-creator.general.error'))
        return
      }

      const zipFileCreated = await createAndDownloadZip({
        text: this.text ?? null,
        imagePath: this.image ?? null,
        carouselImages: this.carouselImages?.length > 0 ? this.carouselImages : null,
        textKind: this.$t(
          `content-creator.text-kind-types.${this.transformToKebabCase(this.textKind)}`
        ),
        topic: this.topic,
        url: buildUrlWithTracking(this.url, this.textKind, this.imageSource),
      })

      if (!zipFileCreated) {
        showSnackbarMessage('error', this.$t('alerts.content-creator.create-zip-file.error'))
      }
    },
  },
  emits: ['contentSaved'],
}
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

.download-btn:after {
  text-decoration: underline;
}

:deep(fieldset) {
  min-width: 64px;
  padding: 0 16px;
  border-radius: 4px;
  border-color: rgba(0, 0, 0, 0.12);
}

.v-btn:not(.v-btn--round).v-size--default {
  height: 40px;
}

.download-post-btn-in-history {
  height: 40px !important;
  padding: 0 8px !important;
  font-size: 10px !important;
}

@media (max-width: 960px) {
  .download-post-btn-in-history {
    width: 100%;
  }
}
</style>
